import { BFF_SERVICE } from '../../../../../../utils/constants'
import storage from '../../../../../../utils/storageService'
import { ImportQuestion, MAX_MULTI } from './types'

const auth = () => ({ Authorization: `Bearer ${storage?.get('token')}` })

let seq = 0
export const nextKey = () => ++seq

// Строка NDJSON от bff -> вопрос; { error } - сбой ИИ посреди потока
const toQuestion = (line: string): ImportQuestion | null => {
    const t = line.trim()
    if (!t.startsWith('{')) return null
    const x = JSON.parse(t)
    if (x.error) throw new Error(x.error)
    return {
        key: nextKey(),
        type: x.type,
        text: x.text,
        opts: x.opts,
        subs: x.subs,
        correct: x.correct,
        hasImage: !!x.hasImage,
        imageNote: x.imageNote,
        screenshot: x.screenshot,
        ai: !!x.ai,
    }
}

// Вопросы приходят по одному, пока ИИ ещё читает: onQuestion вызывается на каждую строку потока
export async function streamParse(
    text: string,
    files: File[],
    onQuestion: (q: ImportQuestion) => void,
    signal: AbortSignal,
) {
    const body = new FormData()
    if (text) body.append('text', text)
    files.forEach((f) => body.append('images', f))
    const res = await fetch(`${BFF_SERVICE}/question/import/parse`, {
        method: 'POST',
        headers: auth(),
        body,
        signal,
    })
    if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`)

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buf = ''
    for (;;) {
        const { done, value } = await reader.read()
        buf += decoder.decode(value || new Uint8Array(), { stream: !done })
        let nl: number
        while ((nl = buf.indexOf('\n')) >= 0) {
            const q = toQuestion(buf.slice(0, nl))
            if (q) onQuestion(q)
            buf = buf.slice(nl + 1)
        }
        if (done) break
    }
    const last = toQuestion(buf)
    if (last) onQuestion(last)
}

// Подбор ответов для вопросов без ответа; ИИ-ответ потом кладётся только в ещё пустые вопросы
export async function solveAnswers(
    questions: ImportQuestion[],
    signal: AbortSignal,
): Promise<Array<{ id: number; answer: string[] }>> {
    const res = await fetch(`${BFF_SERVICE}/question/import/solve`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify(
            questions.map((q) => ({
                id: q.key,
                type: q.type,
                text: q.text,
                options: q.opts.map((o) => o[1]),
                matchItems: q.subs?.map((s) => s[1]),
                hasImage: q.hasImage,
            })),
        ),
        signal,
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return (await res.json()).data || []
}

// Ответ ИИ -> correct вопроса (single: 1 буква, multi: до MAX_MULTI, match: буква на каждую сторону)
export const applyAnswer = (q: ImportQuestion, answer: string[]): ImportQuestion => {
    if (q.type === 'match') {
        const correct: Record<string, string> = {}
        ;(q.subs || []).forEach((s, i) => {
            if (answer[i]) correct[s[0]] = answer[i]
        })
        return { ...q, correct, ai: true }
    }
    return { ...q, correct: answer.slice(0, q.type === 'single' ? 1 : MAX_MULTI), ai: true }
}

// В bff нет ручки загрузки картинок для редактора (см. knowledge/dostyq/test-questions-images.md):
// существующие вопросы хранят картинки как data URI в тексте, делаем так же
export const imageToDataUri = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result))
        reader.onerror = () => reject(reader.error)
        reader.readAsDataURL(file)
    })
